import * as React from "react"
import { CloudOff, RefreshCw } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { describeApiError } from "@/lib/apiError"
import { markErrorShown } from "@/lib/shownErrors"
import { useTranslation } from "@/hooks/useTranslation"
import { localizeServerText } from "@shared/serverText"

/**
 * What a screen shows when its data did not arrive — PropPay's LoadError (E5).
 *
 * WHY IT IS NOT AN EmptyState WITH DIFFERENT WORDS
 * Every list page here defaults its data to `[]`, so a query that FAILED
 * renders exactly like a project that is genuinely empty — and then states that
 * emptiness as fact. Stopping the server on 2026-09-10 made it plain: Tasks
 * said "Overdue 0 · Total 0" above "No tasks yet", and Availability said "No
 * phases found for this project". A salesperson with a dozen overdue follow-ups
 * would have been told they had none.
 *
 * What it guarantees:
 *  · `role="alert"` — a failure is announced, not merely drawn.
 *  · a READABLE reason. Given `error`, the sentence comes from
 *    `describeApiError`, so a dropped connection says "Couldn't reach SaleFish
 *    — check your connection" and never the engine's "Failed to fetch" or a
 *    status line with a JSON body. `description` overrides it.
 *  · a way back. With the query client's retries exhausted a failed query
 *    stays failed until something asks again; `onRetry` is that something,
 *    and `retrying` turns the button into "Retrying…" with a turning icon so a
 *    second press is not the reader's only evidence it did anything.
 */
export interface LoadErrorProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** What could not be loaded. Sentence case, no trailing period. */
  title?: React.ReactNode
  /** Whatever the query threw — turned into a sentence by describeApiError. */
  error?: unknown
  /** Said instead of the error's own sentence. */
  description?: React.ReactNode
  /** The sentence when `error` carries nothing readable. */
  fallback?: string
  onRetry?: () => void
  /** A refetch is in flight: the button says "Retrying…" and waits. */
  retrying?: boolean
  retryLabel?: string
  /** Wrap in a Card (default). `false` inside a CardContent or a table body. */
  asCard?: boolean
  size?: "default" | "compact"
  /** Heading level of the title. h2 under a page's h1 by default. */
  headingLevel?: 2 | 3 | 4
}

export const LOAD_ERROR_FALLBACK = "The server did not respond. Nothing has been lost — try again."

export function LoadError({
  title,
  error,
  description,
  fallback: fallbackProp,
  onRetry,
  retrying = false,
  retryLabel,
  asCard = true,
  size = "default",
  headingLevel = 2,
  className,
  ...props
}: LoadErrorProps) {
  const t = useTranslation()
  // This panel says it: the query cache's own toast stays quiet (lib/shownErrors.ts).
  // Marked during render, so it is set before the cache's deferred toast looks.
  markErrorShown(error)
  const fallback = fallbackProp ?? t("uiLoadErrorFallback", "The server did not respond. Nothing has been lost — try again.")
  const Heading = `h${headingLevel}` as "h2" | "h3" | "h4"
  const compact = size === "compact"
  const raw = description ?? (error === undefined ? fallback : describeApiError(error, fallback))
  // The server's own message, in the reader's language when it is a known one.
  const reason = typeof raw === "string" ? localizeServerText(raw, t) : raw

  const body = (
    <div
      role="alert"
      /* Deliberately NOT data-empty-state: scripts/fixture-audit.mjs reads that
         marker to find surfaces showing nothing, and a failure is a different
         finding from an empty one. */
      data-error-state=""
      className={cn(
        "flex flex-col items-center justify-center text-center",
        compact ? "gap-2 px-4 py-8" : "gap-3 px-6 py-10",
        className
      )}
      {...props}
    >
      <CloudOff
        aria-hidden="true"
        className={cn("text-muted-foreground", compact ? "size-6" : "size-7")}
      />
      <div className="space-y-1">
        <Heading className={cn("font-semibold text-foreground", compact ? "text-body" : "text-body-lg")}>
          {title ?? t("uiLoadErrorTitle", "Couldn't load this")}
        </Heading>
        <div className={cn("mx-auto max-w-md text-muted-foreground", compact ? "text-label" : "text-body")}>
          {reason}
        </div>
      </div>
      {onRetry && (
        <Button
          variant="outline"
          size={compact ? "sm" : "default"}
          className="mt-1"
          onClick={onRetry}
          disabled={retrying}
          aria-busy={retrying || undefined}
          data-testid="button-retry-load"
        >
          <RefreshCw aria-hidden="true" className={cn(retrying && "animate-spin")} />
          {retrying ? t("uiRetrying", "Retrying…") : (retryLabel ?? t("uiTryAgain", "Try Again"))}

        </Button>
      )}
    </div>
  )

  if (!asCard) return body
  return (
    <Card>
      <CardContent className="p-0">{body}</CardContent>
    </Card>
  )
}
LoadError.displayName = "LoadError"
